import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const api = axios.create({ baseURL: '/api/super-admin' });
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ─── Styled Components ────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #0f0f14;
  color: #e8e8f0;
  font-family: 'Segoe UI', system-ui, sans-serif;
`;

const TopBar = styled.div`
  background: #1a1a2e;
  border-bottom: 1px solid #2a2a4a;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #7c6aff;
  display: flex;
  align-items: center;
  gap: 10px;
  span { color: #e8e8f0; }
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #888;
  button {
    background: transparent;
    border: 1px solid #333;
    color: #aaa;
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    &:hover { border-color: #7c6aff; color: #7c6aff; }
  }
`;

const Content = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: #1a1a2e;
  border: 1px solid #2a2a4a;
  border-radius: 12px;
  padding: 20px;
  .label { font-size: 12px; color: #555; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
  .value { font-size: 30px; font-weight: 700; color: ${p => p.color || '#7c6aff'}; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  h2 { font-size: 18px; font-weight: 600; color: #e8e8f0; margin: 0; }
`;

const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const Spinner = styled.div`
  width: 20px; height: 20px;
  border: 2px solid #2a2a4a;
  border-top-color: #7c6aff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  margin: 60px auto;
`;

const TenantGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const TenantCard = styled.div`
  background: #1a1a2e;
  border: 1px solid ${p => p.suspended ? '#3a1a1a' : p.expired ? '#2a2210' : '#2a2a4a'};
  border-radius: 14px;
  padding: 24px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: start;
`;

const TenantInfo = styled.div`
  .name { font-size: 17px; font-weight: 600; color: #e8e8f0; margin-bottom: 4px; }
  .email { font-size: 13px; color: #666; margin-bottom: 12px; }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
`;

const MetaItem = styled.div`
  .label { font-size: 11px; color: #444; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 3px; }
  .value { font-size: 14px; color: ${p => p.warn ? '#f59e0b' : '#c8c8d8'}; font-weight: ${p => p.warn ? '600' : '400'}; }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${p =>
    p.status === 'active' ? 'rgba(34,197,94,0.15)' :
    p.status === 'suspended' ? 'rgba(239,68,68,0.15)' :
    p.status === 'expired' ? 'rgba(245,158,11,0.15)' :
    p.status === 'unmanaged' ? 'rgba(99,102,241,0.15)' :
    'rgba(100,100,120,0.2)'};
  color: ${p =>
    p.status === 'active' ? '#22c55e' :
    p.status === 'suspended' ? '#ef4444' :
    p.status === 'expired' ? '#f59e0b' :
    p.status === 'unmanaged' ? '#818cf8' :
    '#666'};
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const KeyBox = styled.code`
  background: #0f0f1a;
  border: 1px solid #2a2a4a;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: #7c6aff;
  letter-spacing: 0.05em;
  cursor: pointer;
  &:hover { border-color: #7c6aff; }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
`;

const Btn = styled.button`
  padding: ${p => p.sm ? '6px 14px' : '10px 22px'};
  border-radius: 8px;
  border: ${p => p.outline ? '1px solid #333' : 'none'};
  background: ${p =>
    p.danger ? '#ef4444' :
    p.success ? '#22c55e' :
    p.outline ? 'transparent' :
    p.secondary ? '#252540' :
    '#7c6aff'};
  color: ${p => p.outline ? '#aaa' : '#fff'};
  font-size: ${p => p.sm ? '12px' : '14px'};
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const UsageBar = styled.div`
  background: #16162a;
  border-radius: 4px;
  height: 5px;
  width: 80px;
  overflow: hidden;
  div {
    height: 100%;
    width: ${p => Math.min(100, (p.used / p.max) * 100)}%;
    background: ${p => (p.used / p.max) >= 1 ? '#ef4444' : (p.used / p.max) >= 0.8 ? '#f59e0b' : '#22c55e'};
    border-radius: 4px;
    transition: width 0.3s;
  }
`;

// ─── Overlay / Modal ──────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.75);
  z-index: 100;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
`;
const Modal = styled.div`
  background: #1a1a2e;
  border: 1px solid #2a2a4a;
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  h3 { margin: 0 0 24px; font-size: 18px; }
`;
const Field = styled.div`
  margin-bottom: 16px;
  label { display: block; font-size: 12px; color: #888; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em; }
  input, textarea, select {
    width: 100%;
    background: #0f0f1a;
    border: 1px solid #2a2a4a;
    border-radius: 8px;
    padding: 10px 14px;
    color: #e8e8f0;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
    &:focus { border-color: #7c6aff; }
  }
  textarea { resize: vertical; min-height: 70px; }
  small { color: #555; font-size: 11px; margin-top: 4px; display: block; }
`;
const ModalActions = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px;
`;
const Divider = styled.div`
  border-top: 1px solid #2a2a4a;
  margin: 20px 0;
`;

// ─── Create Tenant Modal ──────────────────────────────────────────────────────
function CreateTenantModal({ onClose, onCreated }) {
  const { t: tr } = useTranslation();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '', employeeId: '',
    maxStores: 1, maxLabs: 0, maxUsersPerStore: 0, expiresAt: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.password || !form.employeeId) {
      toast.error('All account fields are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        maxStores: Number(form.maxStores),
        maxLabs: Number(form.maxLabs),
        maxUsersPerStore: Number(form.maxUsersPerStore),
        expiresAt: form.expiresAt || null
      };
      await api.post('/tenants', payload, authHeader());
      toast.success('Tenant created! They can now log in.');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error creating tenant');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <h3>{tr('superAdmin.createNewTenantClient')}</h3>
        <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>
          {tr('superAdmin.thisCreatesAnAdminAccount')} <strong style={{ color: '#aaa' }}>{tr('superAdmin.adminLogin')}</strong> {tr('superAdmin.withTheirEmailAndPassword')}
        </p>
        <Divider />
        <p style={{ color: '#7c6aff', fontSize: 12, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('superAdmin.loginCredentials')}</p>
        <Field>
          <label>{tr('superAdmin.firstName')}</label>
          <input value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder={tr('superAdmin.ahmed')} />
        </Field>
        <Field>
          <label>{tr('superAdmin.lastName')}</label>
          <input value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder={tr('superAdmin.benali')} />
        </Field>
        <Field>
          <label>{tr('superAdmin.emailTheyLogInWith')}</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="client@labcentral.dz" />
        </Field>
        <Field>
          <label>{tr('superAdmin.password')}</label>
          <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={tr('superAdmin.strongPassword')} />
        </Field>
        <Field>
          <label>{tr('superAdmin.employeeAdminId')}</label>
          <input value={form.employeeId} onChange={e => set('employeeId', e.target.value)} placeholder={tr('superAdmin.admin001')} />
          <small>{tr('superAdmin.usedInternallyCanBeAnything')}</small>
        </Field>
        <Divider />
        <p style={{ color: '#7c6aff', fontSize: 12, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('superAdmin.whatTheyReAllowedTo')}</p>
        <Field>
          <label>{tr('superAdmin.maxStores')}</label>
          <input type="number" min="0" value={form.maxStores} onChange={e => set('maxStores', e.target.value)} />
          <small>{tr('superAdmin.howManyStoresBranchesThis')}</small>
        </Field>
        <Field>
          <label>{tr('superAdmin.maxLabsFacilities')}</label>
          <input type="number" min="0" value={form.maxLabs} onChange={e => set('maxLabs', e.target.value)} />
          <small>{tr('superAdmin.howManyLabsFacilitiesThey')}</small>
        </Field>
        <Field>
          <label>{tr('superAdmin.maxUsersPerStore0')}</label>
          <input type="number" min="0" value={form.maxUsersPerStore} onChange={e => set('maxUsersPerStore', e.target.value)} />
        </Field>
        <Divider />
        <p style={{ color: '#7c6aff', fontSize: 12, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tr('superAdmin.subscription')}</p>
        <Field>
          <label>{tr('superAdmin.expiryDateLeaveBlankNever')}</label>
          <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.internalNotes')}</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder={tr('superAdmin.paymentPlanContractNotes')} />
        </Field>
        <ModalActions>
          <Btn outline onClick={onClose}>{tr('superAdmin.cancel')}</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? tr('superAdmin.creating') : tr('superAdmin.createTenant')}</Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}

// ─── Edit Limits Modal ────────────────────────────────────────────────────────
function EditLimitsModal({ tenant, onClose, onSaved }) {
  const { t: tr } = useTranslation();
  const [form, setForm] = useState({
    maxStores: tenant.maxStores,
    maxLabs: tenant.maxLabs,
    maxUsersPerStore: tenant.maxUsersPerStore,
    expiresAt: tenant.expiresAt ? tenant.expiresAt.slice(0, 10) : '',
    notes: tenant.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.licenseId}`, {
        ...form,
        maxStores: Number(form.maxStores),
        maxLabs: Number(form.maxLabs),
        maxUsersPerStore: Number(form.maxUsersPerStore),
        expiresAt: form.expiresAt || null
      }, authHeader());
      toast.success('Limits updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <h3>{tr('superAdmin.editLimits')} {tenant.adminUser?.firstName} {tenant.adminUser?.lastName}</h3>
        <Field>
          <label>{tr('superAdmin.maxStores')}</label>
          <input type="number" min="0" value={form.maxStores} onChange={e => set('maxStores', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.maxLabsFacilities')}</label>
          <input type="number" min="0" value={form.maxLabs} onChange={e => set('maxLabs', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.maxUsersPerStore0')}</label>
          <input type="number" min="0" value={form.maxUsersPerStore} onChange={e => set('maxUsersPerStore', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.expiryDate')}</label>
          <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.notes')}</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
        </Field>
        <ModalActions>
          <Btn outline onClick={onClose}>{tr('superAdmin.cancel')}</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? tr('superAdmin.saving') : tr('superAdmin.saveChanges')}</Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}

// ─── Edit Admin Modal ─────────────────────────────────────────────────────────
function EditAdminModal({ tenant, onClose, onSaved }) {
  const { t: tr } = useTranslation();
  const [form, setForm] = useState({
    firstName: tenant.adminUser?.firstName || '',
    lastName: tenant.adminUser?.lastName || '',
    email: tenant.adminUser?.email || ''
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast.error('First name, last name, and email are required');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.licenseId}/admin`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase()
      }, authHeader());
      toast.success('Admin account updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating admin');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <h3>{tr('superAdmin.editAdmin')} {tenant.adminUser?.firstName} {tenant.adminUser?.lastName}</h3>
        <Field>
          <label>{tr('superAdmin.firstName2')}</label>
          <input value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder={tr('superAdmin.firstName3')} />
        </Field>
        <Field>
          <label>{tr('superAdmin.lastName2')}</label>
          <input value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder={tr('superAdmin.lastName3')} />
        </Field>
        <Field>
          <label>{tr('superAdmin.email')}</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="admin@example.com" />
          <small>{tr('superAdmin.thisChangesTheEmailThey')}</small>
        </Field>
        <ModalActions>
          <Btn outline onClick={onClose}>{tr('superAdmin.cancel')}</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? tr('superAdmin.saving') : tr('superAdmin.saveAdmin')}</Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}

// ─── Reset Password Modal ─────────────────────────────────────────────────────
function ResetPasswordModal({ tenant, onClose, onSaved }) {
  const { t: tr } = useTranslation();
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.licenseId}/admin`, {
        password,
        email: tenant.adminUser?.email,
        firstName: tenant.adminUser?.firstName,
        lastName: tenant.adminUser?.lastName
      }, authHeader());
      toast.success('Password updated');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <h3>{tr('superAdmin.resetPassword')} {tenant.adminUser?.firstName} {tenant.adminUser?.lastName}</h3>
        <p style={{ color: '#666', fontSize: 13 }}>{tr('superAdmin.enterANewPasswordFor')} {tenant.adminUser?.email}</p>
        <Field>
          <label>{tr('superAdmin.newPassword')}</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={tr('superAdmin.min6Characters')} />
        </Field>
        <ModalActions>
          <Btn outline onClick={onClose}>{tr('superAdmin.cancel')}</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? tr('superAdmin.saving') : tr('superAdmin.setPassword')}</Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}

// ─── Attach License Modal (for existing admins without a license) ────────────
function AttachLicenseModal({ admin, onClose, onDone }) {
  const { t: tr } = useTranslation();
  const [form, setForm] = useState({
    maxStores: 1, maxLabs: 0, maxUsersPerStore: 0, expiresAt: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admins/${admin._id}/license`, {
        ...form,
        maxStores: Number(form.maxStores),
        maxLabs: Number(form.maxLabs),
        maxUsersPerStore: Number(form.maxUsersPerStore),
        expiresAt: form.expiresAt || null
      }, authHeader());
      toast.success('License attached');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <h3>{tr('superAdmin.attachLicense')} {admin.firstName} {admin.lastName}</h3>
        <p style={{ color: '#666', fontSize: 13 }}>
          {tr('superAdmin.thisExistingAdmin')}{admin.email}{tr('superAdmin.currentlyHasNoLimitsAttaching')}
        </p>
        <Field>
          <label>{tr('superAdmin.maxStores')}</label>
          <input type="number" min="0" value={form.maxStores} onChange={e => set('maxStores', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.maxLabs')}</label>
          <input type="number" min="0" value={form.maxLabs} onChange={e => set('maxLabs', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.maxUsersPerStore0')}</label>
          <input type="number" min="0" value={form.maxUsersPerStore} onChange={e => set('maxUsersPerStore', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.expiryDateBlankNever')}</label>
          <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
        </Field>
        <Field>
          <label>{tr('superAdmin.notes')}</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
        </Field>
        <ModalActions>
          <Btn outline onClick={onClose}>{tr('superAdmin.cancel')}</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? tr('superAdmin.saving') : tr('superAdmin.attachLicense2')}</Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}

// ─── Suspend Modal ────────────────────────────────────────────────────────────
function SuspendModal({ tenant, onClose, onDone }) {
  const { t: tr } = useTranslation();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    setLoading(true);
    try {
      await api.put(`/tenants/${tenant.licenseId}/suspend`, { reason }, authHeader());
      toast.success('Tenant suspended');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setLoading(false); }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <h3 style={{ color: '#ef4444' }}>{tr('superAdmin.suspendAccess')}</h3>
        <p style={{ color: '#888', marginTop: 0 }}>
          <strong style={{ color: '#e8e8f0' }}>{tenant.adminUser?.firstName} {tenant.adminUser?.lastName}</strong> ({tenant.adminUser?.email}{tr('superAdmin.willNotBeAbleTo')}
        </p>
        <Field>
          <label>{tr('superAdmin.reasonShownToThemOn')}</label>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder={tr('superAdmin.paymentOverdueContractEnded')} />
        </Field>
        <ModalActions>
          <Btn outline onClick={onClose}>{tr('superAdmin.cancel')}</Btn>
          <Btn danger onClick={confirm} disabled={loading}>{loading ? tr('superAdmin.suspending') : tr('superAdmin.suspend')}</Btn>
        </ModalActions>
      </Modal>
    </Overlay>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  const { t: tr } = useTranslation();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [adminEditTarget, setAdminEditTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [attachTarget, setAttachTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);

  const userName = (() => {
    try { return JSON.parse(localStorage.getItem('user'))?.fullName || 'Super Admin'; } catch { return 'Super Admin'; }
  })();

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, tenantsRes] = await Promise.all([
        api.get('/stats', authHeader()),
        api.get('/tenants', authHeader()),
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data.tenants);
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load data';
      console.error('Failed to load super admin data:', err.response?.data || err);
      toast.error(message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('License key copied');
  };

  const activate = async (tenant) => {
    try {
      await api.put(`/tenants/${tenant.licenseId}/activate`, {}, authHeader());
      toast.success('Tenant reactivated');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const regenerateKey = async (tenant) => {
    if (!window.confirm(`Regenerate key for ${tenant.adminUser?.email}? Old key stops working.`)) return;
    try {
      const res = await api.post(`/tenants/${tenant.licenseId}/regenerate-key`, {}, authHeader());
      toast.success('New key: ' + res.data.key);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const runDiagnostic = async () => {
    try {
      const res = await api.get('/diagnostics/databases', authHeader());
      setDiagnostic(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Diagnostic failed');
    }
  };

  const deleteTenant = async (tenant) => {
    if (!window.confirm(`DELETE tenant ${tenant.adminUser?.email}? This removes their account and license permanently.`)) return;
    try {
      await api.delete(`/tenants/${tenant.licenseId}`, authHeader());
      toast.success('Tenant deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const filtered = tenants.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.adminUser?.firstName?.toLowerCase().includes(q) ||
      t.adminUser?.lastName?.toLowerCase().includes(q) ||
      t.adminUser?.email?.toLowerCase().includes(q)
    );
  });

  return (
    <Page>
      <Toaster position="top-right" toastOptions={{ style: { background: '#1a1a2e', color: '#e8e8f0', border: '1px solid #2a2a4a' } }} />

      <TopBar>
        <Logo>⚡ <span>{tr('superAdmin.superAdmin')}</span></Logo>
        <UserBadge>
          <span>{userName}</span>
          <button onClick={logout}>{tr('superAdmin.logout')}</button>
        </UserBadge>
      </TopBar>

      <Content>
        {/* Stats */}
        <StatsGrid>
          <StatCard color="#7c6aff">
            <div className="label">{tr('superAdmin.totalClients')}</div>
            <div className="value">{stats?.totalTenants ?? '—'}</div>
          </StatCard>
          <StatCard color="#22c55e">
            <div className="label">{tr('superAdmin.active')}</div>
            <div className="value">{stats?.activeTenants ?? '—'}</div>
          </StatCard>
          <StatCard color="#ef4444">
            <div className="label">{tr('superAdmin.suspended')}</div>
            <div className="value">{stats?.suspendedTenants ?? '—'}</div>
          </StatCard>
          <StatCard color="#38bdf8">
            <div className="label">{tr('superAdmin.totalStores')}</div>
            <div className="value">{stats?.totalStores ?? '—'}</div>
          </StatCard>
          <StatCard color="#a78bfa">
            <div className="label">{tr('superAdmin.totalLabs')}</div>
            <div className="value">{stats?.totalLabs ?? '—'}</div>
          </StatCard>
        </StatsGrid>

        {/* Tenants List */}
        <Header>
          <h2>{tr('superAdmin.clientsTenants')}</h2>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={tr('superAdmin.searchByNameOrEmail')}
              style={{
                background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8,
                padding: '8px 14px', color: '#e8e8f0', fontSize: 14, outline: 'none', width: 240
              }}
            />
            <Btn secondary onClick={runDiagnostic} title={tr('superAdmin.showAllTenantDatabasesAnd')}>{tr('superAdmin.diagnostic')}</Btn>
            <Btn onClick={() => setShowCreate(true)}>{tr('superAdmin.newClient')}</Btn>
          </div>
        </Header>

        {loading ? <Spinner /> : (
          <TenantGrid>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: '#333', padding: '60px', background: '#1a1a2e', borderRadius: 14, border: '1px solid #2a2a4a' }}>
                {search ? tr('superAdmin.noClientsMatchYourSearch') : tr('superAdmin.noClientsYetClickNew')}
              </div>
            )}
            {filtered.map(t => {
              const storesAtLimit = t.maxStores > 0 && t.usedStores >= t.maxStores;
              const labsAtLimit = t.maxLabs > 0 && t.usedLabs >= t.maxLabs;
              return (
                <TenantCard key={t.licenseId} suspended={t.status === 'suspended'} expired={t.status === 'expired'}>
                  <TenantInfo>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <div className="name">{t.adminUser?.firstName} {t.adminUser?.lastName}</div>
                      <Badge status={t.status}>{t.status}</Badge>
                    </div>
                    <div className="email">{t.adminUser?.email}</div>

                    <MetaRow>
                      {t.unmanaged ? (
                        <>
                          <MetaItem>
                            <div className="label">{tr('superAdmin.license')}</div>
                            <div className="value" style={{ color: '#818cf8' }}>{tr('superAdmin.noLicenseUnlimited')}</div>
                          </MetaItem>
                          <MetaItem>
                            <div className="label">{tr('superAdmin.lastLogin')}</div>
                            <div className="value">{t.adminUser?.lastLogin ? new Date(t.adminUser.lastLogin).toLocaleDateString() : tr('superAdmin.never')}</div>
                          </MetaItem>
                          <MetaItem>
                            <div className="label">{tr('superAdmin.created')}</div>
                            <div className="value">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}</div>
                          </MetaItem>
                        </>
                      ) : (
                        <>
                          <MetaItem warn={storesAtLimit}>
                            <div className="label">{tr('superAdmin.stores')}</div>
                            <div className="value">{t.usedStores} / {t.maxStores === 0 ? '∞' : t.maxStores}</div>
                            {t.maxStores > 0 && (
                              <UsageBar used={t.usedStores} max={t.maxStores}><div /></UsageBar>
                            )}
                          </MetaItem>
                          <MetaItem warn={labsAtLimit}>
                            <div className="label">{tr('superAdmin.labs')}</div>
                            <div className="value">{t.usedLabs} / {t.maxLabs === 0 ? tr('superAdmin.nA') : t.maxLabs}</div>
                            {t.maxLabs > 0 && (
                              <UsageBar used={t.usedLabs} max={t.maxLabs}><div /></UsageBar>
                            )}
                          </MetaItem>
                          <MetaItem>
                            <div className="label">{tr('superAdmin.expires')}</div>
                            <div className="value">{t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : tr('superAdmin.never')}</div>
                          </MetaItem>
                          <MetaItem>
                            <div className="label">{tr('superAdmin.lastLogin')}</div>
                            <div className="value">{t.adminUser?.lastLogin ? new Date(t.adminUser.lastLogin).toLocaleDateString() : tr('superAdmin.never')}</div>
                          </MetaItem>
                          <MetaItem>
                            <div className="label">{tr('superAdmin.licenseKey')}</div>
                            <KeyBox onClick={() => copyKey(t.key)} title={tr('superAdmin.clickToCopy')}>{t.key}</KeyBox>
                          </MetaItem>
                        </>
                      )}
                    </MetaRow>

                    {t.status === 'suspended' && t.suspendReason && (
                      <div style={{ marginTop: 10, fontSize: 12, color: '#ef4444', background: 'rgba(239,68,68,0.08)', padding: '6px 12px', borderRadius: 6 }}>
                        {tr('superAdmin.suspended2')} {t.suspendReason}
                      </div>
                    )}
                    {t.notes && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>{tr('superAdmin.note')} {t.notes}</div>
                    )}
                  </TenantInfo>

                  <Actions>
                    {t.unmanaged ? (
                      <BtnRow>
                        <Btn sm onClick={() => setAttachTarget(t.adminUser)}>{tr('superAdmin.attachLicense2')}</Btn>
                      </BtnRow>
                    ) : (
                      <>
                        <BtnRow>
                          <Btn sm secondary onClick={() => setAdminEditTarget(t)}>{tr('superAdmin.editAdmin2')}</Btn>
                          <Btn sm secondary onClick={() => setEditTarget(t)}>{tr('superAdmin.editLimits2')}</Btn>
                          <Btn sm secondary onClick={() => setResetTarget(t)}>{tr('superAdmin.resetPassword2')}</Btn>
                        </BtnRow>
                        <BtnRow>
                          <Btn sm secondary onClick={() => regenerateKey(t)} title={tr('superAdmin.regenerateLicenseKey')}>{tr('superAdmin.newKey')}</Btn>
                          {t.status === 'suspended'
                            ? <Btn sm success onClick={() => activate(t)}>{tr('superAdmin.activate')}</Btn>
                            : <Btn sm danger onClick={() => setSuspendTarget(t)}>{tr('superAdmin.suspend')}</Btn>
                          }
                        </BtnRow>
                        <BtnRow>
                          <Btn sm outline onClick={() => deleteTenant(t)} style={{ color: '#ef4444', borderColor: '#3a1a1a' }}>{tr('superAdmin.delete')}</Btn>
                        </BtnRow>
                      </>
                    )}
                  </Actions>
                </TenantCard>
              );
            })}
          </TenantGrid>
        )}
      </Content>

      {diagnostic && (
        <Overlay onClick={e => e.target === e.currentTarget && setDiagnostic(null)}>
          <Modal style={{ maxWidth: 720 }}>
            <h3>{tr('superAdmin.databaseIsolationDiagnostic')}</h3>
            <div style={{ marginBottom: 16, padding: '12px 14px', background: '#0f0f1a', border: '1px solid #2a2a4a', borderRadius: 8, fontSize: 13 }}>
              <div><span style={{ color: '#666' }}>{tr('superAdmin.controlDb')}</span> <code style={{ color: '#7c6aff' }}>{diagnostic.controlDatabase}</code></div>
              <div><span style={{ color: '#666' }}>{tr('superAdmin.totalMongodbDatabases')}</span> {diagnostic.totalDatabases}</div>
            </div>
            <h4 style={{ color: '#aaa', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 24 }}>{tr('superAdmin.tenants')}</h4>
            {diagnostic.tenants.length === 0 && <p style={{ color: '#555' }}>{tr('superAdmin.noTenantsYet')}</p>}
            {diagnostic.tenants.map((t, i) => (
              <div key={i} style={{ padding: 12, background: '#0f0f1a', border: '1px solid #2a2a4a', borderRadius: 8, marginBottom: 8, fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{t.client} <span style={{ color: '#666', fontWeight: 400 }}>({t.email})</span></div>
                <div style={{ marginTop: 4, color: '#888' }}>{tr('superAdmin.db')} <code style={{ color: t.databaseExists ? '#22c55e' : '#ef4444' }}>{t.tenantDatabaseName || '(none)'}</code> {t.databaseExists ? '✓' : '✗'}</div>
                <div style={{ color: '#666', fontSize: 12 }}>{tr('superAdmin.collections')} {t.collectionCount} {tr('superAdmin.status')} {t.status} {tr('superAdmin.key')} {t.licenseKey}</div>
              </div>
            ))}
            {diagnostic.orphanDatabases?.length > 0 && (
              <>
                <h4 style={{ color: '#f59e0b', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 24 }}>{tr('superAdmin.orphanDatabasesTenantWithNo')}</h4>
                {diagnostic.orphanDatabases.map((d, i) => (
                  <div key={i} style={{ padding: 8, fontSize: 12, color: '#888' }}>{d.name} ({(d.sizeOnDisk / 1024).toFixed(1)} {tr('superAdmin.kb')}</div>
                ))}
              </>
            )}
            <ModalActions>
              <Btn onClick={() => setDiagnostic(null)}>{tr('superAdmin.close')}</Btn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}

      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editTarget && <EditLimitsModal tenant={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      {adminEditTarget && <EditAdminModal tenant={adminEditTarget} onClose={() => setAdminEditTarget(null)} onSaved={() => { setAdminEditTarget(null); load(); }} />}
      {suspendTarget && <SuspendModal tenant={suspendTarget} onClose={() => setSuspendTarget(null)} onDone={() => { setSuspendTarget(null); load(); }} />}
      {resetTarget && <ResetPasswordModal tenant={resetTarget} onClose={() => setResetTarget(null)} />}
      {attachTarget && <AttachLicenseModal admin={attachTarget} onClose={() => setAttachTarget(null)} onDone={() => { setAttachTarget(null); load(); }} />}
    </Page>
  );
}
